import React, { useEffect, useState } from 'react';
import { BarChart3, Clock3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 5 * 60 * 1000;

function formatNum(n) {
    return typeof n === 'number' ? n.toLocaleString('es-CL') : '—';
}

export function VisitCounterBand() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const r = await fetch(`${API}/api/public/visit-stats`);
                if (!r.ok) return;
                const d = await r.json();
                if (alive) setStats(d);
            } catch { /* la landing nunca se rompe por el contador */ }
        };
        load();
        const id = setInterval(load, REFRESH_MS);
        return () => { alive = false; clearInterval(id); };
    }, []);

    if (!stats || stats.sin_datos) return null;

    return (
        <section
            className="mt-6 overflow-hidden rounded-2xl bg-white shadow-[0_20px_60px_-25px_rgba(2,8,23,0.6)] ring-1 ring-white/20"
            data-testid="visit-counter-band"
            aria-label="Visitas a Portafolio Digital Docente"
        >
            <div className="h-1.5 w-full bg-gradient-to-r from-sky-600 via-emerald-500 to-sky-600" />
            <div className="flex flex-col gap-4 px-5 py-5 sm:px-7">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="inline-flex items-center gap-2 font-heading text-base font-bold text-slate-900 md:text-lg" data-testid="visit-counter-title">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-sky-600 text-white">
                            <BarChart3 className="h-4 w-4" />
                        </span>
                        📊 Visitas a Portafolio Digital Docente
                    </h2>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200" data-testid="visit-counter-updated">
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                        </span>
                        En vivo · {stats.actualizado_en_texto} hrs (Chile)
                    </span>
                </div>

                <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 sm:gap-3">
                    {stats.periodos.map((p, i) => (
                        <li
                            key={p.key}
                            className={`relative rounded-xl border px-3 py-3 sm:px-4 transition-transform duration-200 hover:-translate-y-0.5 last:col-span-2 sm:last:col-span-1 ${
                                i % 2 === 0 ? 'border-sky-100 bg-sky-50/70' : 'border-emerald-100 bg-emerald-50/70'
                            }`}
                            data-testid={`visit-counter-${p.key}`}
                        >
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{p.label}</p>
                            <p
                                className={`mt-1 font-mono text-2xl font-extrabold tabular-nums leading-none sm:text-3xl ${
                                    i % 2 === 0 ? 'text-sky-800' : 'text-emerald-800'
                                }`}
                                data-testid={`visit-counter-${p.key}-value`}
                            >
                                {formatNum(p.sesiones)}
                            </p>
                            <p className="mt-1 text-[11px] text-slate-500">
                                {p.sesiones === 1 ? 'sesión de visita' : 'sesiones de visita'}
                            </p>
                            {!p.completo && (
                                <span
                                    className="absolute right-2 top-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-800"
                                    title={`Datos parciales: registros desde ${p.desde}`}
                                    data-testid={`visit-counter-${p.key}-parcial`}
                                >
                                    Parcial
                                </span>
                            )}
                        </li>
                    ))}
                </ul>

                <p className="inline-flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500" data-testid="visit-counter-note">
                    <Clock3 className="h-3 w-3" />
                    Contamos sesiones de visita, no recargas. Datos disponibles desde {stats.datos_desde_fecha}.
                    {stats.periodos.some((p) => !p.completo) && (
                        <span className="text-amber-700">Los períodos marcados «Parcial» aún no cuentan con historial completo.</span>
                    )}
                </p>
            </div>
        </section>
    );
}

export default VisitCounterBand;
